import { isAxiosError } from "axios";

/** Shape returned by the NestJS API for error responses. */
export interface ApiErrorBody {
  message: string | string[];
  error?: string;
  statusCode: number;
}

/** True when the backend rejected the request because the user lacks a required permission
 * (`PermissionsGuard` -> 403). The guard's raw message ("Missing permission(s): <key>") is an
 * internal detail — never show it directly, always route through `getErrorMessage` instead. */
export function isForbidden(error: unknown): boolean {
  return isAxiosError<ApiErrorBody>(error) && error.response?.status === 403;
}

const FORBIDDEN_MESSAGE =
  "Bạn không có quyền sử dụng tính năng này, liên hệ với admin để được cấp quyền!";

/** Normalizes any thrown error (axios or otherwise) into a display string. */
export function getErrorMessage(error: unknown): string {
  if (isForbidden(error)) return FORBIDDEN_MESSAGE;
  if (isAxiosError<ApiErrorBody>(error)) {
    const body = error.response?.data;
    if (body?.message) {
      return Array.isArray(body.message) ? body.message.join(", ") : body.message;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Đã xảy ra lỗi. Vui lòng thử lại.";
}

/**
 * True when the 404 is NestJS's framework-level "no route matches this URL" response
 * (message always "Cannot GET /api/xxx", no `code` field) rather than a business
 * ResourceNotFoundException (which has a `code: "RESOURCE_NOT_FOUND"` and a message about
 * the specific record). The former means the backend for this screen isn't built yet — surface
 * that distinctly instead of leaking the raw framework string, and don't offer a retry button
 * since retrying won't help until the endpoint ships.
 */
export function isRouteNotImplemented(error: unknown): boolean {
  if (!isAxiosError<ApiErrorBody>(error) || error.response?.status !== 404) {
    return false;
  }
  const message = error.response.data?.message;
  const text = Array.isArray(message) ? message.join(", ") : message;
  return typeof text === "string" && /^Cannot [A-Z]+ /.test(text);
}
